import { Injectable } from "@angular/core";
import { AuthService } from "../services/http-services/authservice.service";
import { environment as env } from "src/environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { IQuestionBankQuestion, IQuestionBank, IQuestionBankDto, IQuestionBankGroup } from "../interface/dto";
import { TableParams } from '../components/grid/TableParams';
import { lastValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class QuestionBankService {
  constructor(private httpClient: HttpClient, private auth: AuthService) { }

  configureHeaders() {
    let headers = new HttpHeaders();
    headers = headers.set("Content-Type", "application/json");
    headers = headers.set("Authorization", `Bearer ${this.auth.token}`);
    return headers;
  }

  setSelectedQuestion(question: IQuestionBankQuestion[]) {
    localStorage.setItem("selectedQBQuestions", JSON.stringify(question));
  }

  get selectedQuestions() {
    return JSON.parse(
      localStorage.getItem("selectedQBQuestions")
    ) as IQuestionBankQuestion[];
  }

  get selectedQuestionIds() {
    if (this.selectedQuestions == null || this.selectedQuestions.length === 0) {
      return 0;
    }
    return this.selectedQuestions.map((x) => x.questionBankQuestionID);
  }

  async disableQuestion(questionID: number) {
    return await lastValueFrom(
      this.httpClient.put(
        `${env.apiURL}questionsbank/question/${questionID}/disable`,
        {},
        { headers: this.configureHeaders() }
      ),
      { defaultValue: [] }
    );
  }

  setSelectedBanks(banks: IQuestionBank[]): void {
    sessionStorage.setItem('selectedQuestionBanks', JSON.stringify(banks));
  }

  get selectedQuestionBanks() {
    return JSON.parse(
      sessionStorage.getItem("selectedQuestionBanks")
    ) as IQuestionBank[];
  }

  get selectedQuestionBankIds() {
    if (this.selectedQuestionBanks == null || this.selectedQuestionBanks.length === 0) {
      return [];
    }
    return this.selectedQuestionBanks.map((x) => x.questionBankID);
  }

  async getQuestionBanks(tableParam: TableParams) {
    return await lastValueFrom(
      this.httpClient.post(
        `${env.apiURL}questionsbank/getall`,
        tableParam,
        { headers: this.configureHeaders() }
      ),
      { defaultValue: [] }
    );
  }

  async getQuestionBank(questionBankID: number): Promise<IQuestionBank> {
    return await lastValueFrom(
      this.httpClient.get<IQuestionBank>(
        `${env.apiURL}questionsbank/${questionBankID}`,
        { headers: this.configureHeaders() }
      )
    );
  }

  async createQuestionBank(data: IQuestionBank) {
    return await lastValueFrom(
      this.httpClient.post(`${env.apiURL}questionsbank`, data, {
        headers: this.configureHeaders(),
      }),
      { defaultValue: [] }
    );
  }

  async updateQuestionBank(data: IQuestionBank) {
    return await lastValueFrom(
      this.httpClient.put(`${env.apiURL}questionsbank`, data, {
        headers: this.configureHeaders(),
      }),
      { defaultValue: [] }
    );
  }

  async getQuestionBanksLookup(examType?: string, examID?: number, isPsychometricExam?: boolean): Promise<IQuestionBankDto[]> {
    const params = new URLSearchParams();

    if (examType) {
      params.append('examType', examType);
    }

    if (examID !== undefined && examID !== null) {
      params.append('examID', examID.toString());
    }

    if (isPsychometricExam === true) {
      params.append('isPsychometricExam', "true");
    }
    const queryString = params.toString();
    const url = `${env.apiURL}questionsbank/questionbankstablelookup${queryString ? `?${queryString}` : ''}`;

    return await lastValueFrom(
      this.httpClient.get<IQuestionBankDto[]>(
        url,
        { headers: this.configureHeaders() }
      ),
      { defaultValue: [] }
    );
  }

  async getQuestions(tableParam: TableParams) {
    return await lastValueFrom(
      this.httpClient.post(
        `${env.apiURL}questionsbank/questions`,
        tableParam,
        { headers: this.configureHeaders() }
      ),
      { defaultValue: [] }
    );
  }

  async getQuestion(questionID: number): Promise<IQuestionBankQuestion> {
    return await lastValueFrom(
      this.httpClient.get<IQuestionBankQuestion>(
        `${env.apiURL}questionsbank/question/${questionID}`,
        { headers: this.configureHeaders() }
      )
    );
  }

  async createQuestion(data: IQuestionBankQuestion) {
    return await lastValueFrom(
      this.httpClient.post(`${env.apiURL}questionsbank/add-question`, data, {
        headers: this.configureHeaders(),
      }),
      { defaultValue: [] }
    );
  }

  async updateQuestion(data: IQuestionBankQuestion) {
    return await lastValueFrom(
      this.httpClient.put(`${env.apiURL}questionsbank/update-question`, data, {
        headers: this.configureHeaders(),
      }),
      { defaultValue: [] }
    );
  }
  async uploadQuestionBankQuestions(data: FormData) {
    let header = new HttpHeaders({
      'Content-Disposition': 'multipart/form-data',
      "Authorization": `Bearer ${this.auth.token}`
    });
    return this.httpClient.post(`${env.apiURL}questionsbank/questions/upload`, data, { headers: header }).toPromise();
  }

  async getQuestionBankCompany(): Promise<string> {
    return await lastValueFrom(
      this.httpClient.get(`${env.apiURL}questionsbank/company`, {
        headers: this.configureHeaders(),
        responseType: 'text'
      }),
      { defaultValue: '' }
    );
  }

  async getQuestionBankGroups(tableParam: TableParams) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}questionsbank/question-groups`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }

  async saveQuestionBankGroup(data: IQuestionBankGroup) {
    return this.httpClient.post(`${env.apiURL}questionsbank/question-groups`, data, { headers: this.configureHeaders() }).toPromise();
  }

  async deleteQuestionBankGroup(data: IQuestionBankGroup) {
    return this.httpClient.delete(`${env.apiURL}questionsbank/question-groups?id=${data.id}`, { headers: this.configureHeaders() }).toPromise();
  }

  async getQuestionBankGroupsLookup(): Promise<IQuestionBankGroup[]> {
    return this.httpClient
      .get<IQuestionBankGroup[]>(`${env.apiURL}questionsbank/question-groups-lookup`, {
        headers: this.configureHeaders()
      })
      .toPromise();
  }

}
